import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadReplies } from '../../hooks/useUnreadReplies';
import {
  LayoutDashboard,
  FileText,
  Users,
  MessageSquare,
  TrendingUp,
  Settings,
  Sparkles,
  Layout,
  FolderOpen,
  BarChart3,
  FileBarChart,
  Zap,
  ChevronDown,
  User,
  LogOut,
  Send,
  Mail,
  ShieldCheck,
  Workflow,
  Newspaper
} from 'lucide-react';
import RemindersWidget from '../reminders/RemindersWidget';
import FeedbackWidget from '../feedback/FeedbackWidget';
import profileService from '../../services/ProfileService';

const NewNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { unreadCount } = useUnreadReplies();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [profile, setProfile] = useState(null);
  const dropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  // Load advisor profile so the avatar shows real initials (e.g. "AM"
  // for "Ashish Markanday") instead of falling back to "U" when email
  // isn't available or doesn't follow first.last@ pattern.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const userId = user.userId || user.username || user.sub;
    if (!userId) return;
    (async () => {
      try {
        const result = await profileService.getProfileByUserId(userId);
        if (!cancelled && result) setProfile(result);
      } catch (err) {
        // Non-fatal — initials fall back to email-derived value
        console.warn('NewNavigation: failed to load profile for initials', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavClick = (path) => {
    navigate(path);
    setOpenDropdown(null);
  };

  const handleQuickStart = (templateType) => {
    // Navigate to newsletter creation with template type pre-selected
    navigate('/app/newsletters/create', { state: { quickStart: templateType } });
    setOpenDropdown(null);
  };

  const toggleDropdown = (dropdownName) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  const getUserInitials = () => {
    // Prefer real profile name → email pattern → final 'U' fallback
    const first = profile?.firstName?.trim();
    const last = profile?.lastName?.trim();
    if (first && last) return (first[0] + last[0]).toUpperCase();
    if (first) return first.substring(0, 2).toUpperCase();

    // Issue #298: email lives at user.attributes.email (AuthContext shape),
    // NOT user.email. The old check `user?.email` was always undefined and
    // every user fell through to the 'U' fallback.
    const email = user?.attributes?.email || user?.email;
    if (!email) return 'U';
    const parts = email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Phase 1C (#169): reviewer inbox surfaced only when the compliance approval
  // feature flag is on. Visibility is further gated downstream by the page itself
  // (the user needs at least one PublicationSeries with requiresApprovalBeforeSend=true);
  // gating fully on the page would still surface a dead nav link, so we hide on
  // the flag here and let the page double-check on mount.
  const complianceApprovalFlagOn =
    import.meta.env?.VITE_USE_COMPLIANCE_APPROVAL === 'true';

  // #315 — Drip / Sequence Campaigns MVP. Flag-gated nav link so the
  // page is invisible to non-alpha users (the page itself ALSO gates
  // on the same flag, so a stray direct nav still renders null).
  const dripSequencesFlagOn =
    import.meta.env?.VITE_USE_DRIP_SEQUENCES === 'true';

  const manageItems = [
    {
      icon: Mail,
      label: 'Inbox',
      route: '/app/communications?view=inbox',
      badge: unreadCount > 0 ? String(unreadCount) : null,
      isInbox: true  // Special flag for inbox badge styling
    },
    { icon: BarChart3, label: 'Campaigns', route: '/app/communications?view=campaigns' },
    { icon: Zap, label: 'Activity', route: '/app/communications?view=activity' },
    { icon: Layout, label: 'Templates', route: '/app/templates', highlight: true },
    // #394: the routed SeriesManagementPage had no way in from the nav.
    { icon: Newspaper, label: 'Series', route: '/app/communications/series' },
  ];
  if (complianceApprovalFlagOn) {
    manageItems.push({
      icon: ShieldCheck,
      label: 'Compliance Review',
      route: '/app/communications/review',
    });
  }
  if (dripSequencesFlagOn) {
    manageItems.push({
      icon: Workflow,
      label: 'Sequences',
      route: '/app/communications/sequences',
    });
  }

  const communicationsDropdown = [
    {
      heading: 'CREATE',
      items: [
        {
          icon: Sparkles,
          label: 'New Newsletter',
          route: '/app/communications/campaigns/unified'
          // #395: removed the "Ctrl+N" badge — no handler backed it and
          // Ctrl+N is browser-reserved (opens a new window).
        }
      ]
    },
    {
      heading: 'MANAGE',
      // #395: dropped the user-facing "LEGACY" section. "Newsletter History"
      // was a second view of the same EmailCampaign data as Campaigns
      // (Rule 13), so it's removed; Performance Reports moves under MANAGE.
      items: [
        ...manageItems,
        { icon: FileBarChart, label: 'Performance Reports', route: '/app/newsletters/reports' }
      ],
    }
  ];

  const settingsDropdown = [
    {
      heading: 'PREFERENCES',
      items: [
        { icon: Users, label: 'Profile & Preferences', route: '/app/settings?tab=profile' },
        { icon: Layout, label: 'Branding', route: '/app/settings?tab=branding' }
        // #settings-reorg: removed the "AI Configuration" item — the standalone
        // AI & Content tab was retired; communication style lives under Profile.
      ]
    },
    // Audit 2026-09-06 §04: the "ADVANCED" group (Market Data Sources → a
    // crashing duplicate of Content Hub, System Health → a diagnostic page,
    // Usage Analytics → "Coming Soon") and "Security & Privacy" (→ "Coming
    // Soon") are gone until those screens are real.
  ];

  const navItems = [
    {
      id: 'dashboard',
      icon: LayoutDashboard,
      label: 'Dashboard',
      route: '/app/dashboard'
    },
    {
      id: 'communications',
      icon: Mail,
      label: 'Communications',
      route: '/app/communications?view=campaigns',
      hasDropdown: true,
      dropdown: communicationsDropdown
    },
    {
      id: 'clients',
      icon: Users,
      label: 'Clients',
      route: '/app/clients'
    },
    {
      id: 'content',
      icon: TrendingUp,
      label: 'Content',
      route: '/app/content'
    },
    {
      id: 'settings',
      icon: Settings,
      label: 'Settings',
      route: '/app/settings',
      hasDropdown: true,
      dropdown: settingsDropdown
    }
  ];

  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center mr-8">
            <div className="flex-shrink-0 flex items-center">
              <LayoutDashboard className="h-8 w-8 text-blue-600" />
              {/* #356: one brand everywhere — never "Financial Newsletter Generator" / "FNG". */}
              <span className="ml-2 text-lg font-bold text-gray-900 whitespace-nowrap">
                Finnewsy
              </span>
            </div>
          </div>

          {/* Navigation Items */}
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center space-x-1" ref={dropdownRef}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.route);
              const dropdownOpen = openDropdown === item.id;

              return (
                <div key={item.id} className="relative">
                  <button
                    onClick={() => {
                      if (item.hasDropdown) {
                        toggleDropdown(item.id);
                      } else {
                        handleNavClick(item.route);
                      }
                    }}
                    className={`
                      flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap
                      ${active
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                      }
                    `}
                  >
                    <Icon className="h-5 w-5 mr-1.5 flex-shrink-0" />
                    <span className="hidden md:inline">{item.label}</span>
                    {item.hasDropdown && (
                      <ChevronDown className={`h-4 w-4 ml-1 transition-transform hidden md:inline ${dropdownOpen ? 'rotate-180' : ''}`} />
                    )}
                  </button>

                  {/* Dropdown Menu */}
                  {item.hasDropdown && dropdownOpen && (
                    <div className="absolute left-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                      <div className="py-2">
                        {item.dropdown.map((section, sectionIdx) => (
                          <div key={sectionIdx}>
                            {/* Section Heading */}
                            <div className="px-4 py-2">
                              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                {section.heading}
                              </h3>
                            </div>

                            {/* Section Items */}
                            {section.items.map((menuItem, itemIdx) => {
                              const MenuIcon = menuItem.icon;

                              return (
                                <button
                                  key={itemIdx}
                                  onClick={() => {
                                    if (menuItem.route) {
                                      handleNavClick(menuItem.route);
                                    } else if (menuItem.action) {
                                      // Handle quick start actions
                                      handleQuickStart(menuItem.action);
                                    }
                                  }}
                                  className={`
                                    w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700
                                    hover:bg-gray-50 transition-colors
                                    ${menuItem.highlight ? 'bg-blue-50 hover:bg-blue-100' : ''}
                                  `}
                                >
                                  <div className="flex items-center">
                                    <MenuIcon className={`h-4 w-4 mr-3 ${menuItem.highlight ? 'text-blue-600' : 'text-gray-500'}`} />
                                    <span className={menuItem.highlight ? 'text-blue-700 font-medium' : ''}>
                                      {menuItem.label}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    {menuItem.badge && (
                                      <span className={`
                                        px-2 py-0.5 text-xs rounded-full font-medium
                                        ${menuItem.isInbox
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-gray-200 text-gray-700'
                                        }
                                      `}>
                                        {menuItem.badge}
                                      </span>
                                    )}
                                  </div>
                                </button>
                              );
                            })}

                            {/* Divider between sections */}
                            {sectionIdx < item.dropdown.length - 1 && (
                              <div className="my-2 border-t border-gray-100" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          </div>

          {/* Right side actions */}
          <div className="flex items-center space-x-2 ml-4">
            {/* Reminders Widget */}
            <RemindersWidget />

            {/* Issue #298 follow-up: removed the second Notifications bell —
                it rendered hardcoded mock content ("Newsletter Published" /
                "High Open Rate Alert" / "Draft Saved") with a permanent red
                dot that had no real data backing it (Rule 15 violation).
                RemindersWidget is the single bell now. */}

            {/* #324 — alpha-cohort feedback widget */}
            <FeedbackWidget />

            {/* Profile Dropdown */}
            <div className="relative" ref={profileDropdownRef}>
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                aria-label="User menu"
                aria-expanded={showProfileDropdown}
                className="flex items-center space-x-2 p-2 text-gray-700 hover:bg-gray-100 rounded-md"
              >
                <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium" aria-hidden="true">
                  {getUserInitials()}
                </div>
              </button>

              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                  <div className="p-3 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{user?.attributes?.email || user?.email || 'User'}</p>
                    <p className="text-xs text-gray-500 mt-1">Financial Advisor</p>
                  </div>
                  <div className="py-2">
                    <button
                      onClick={() => {
                        navigate('/app/settings?tab=profile');
                        setShowProfileDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <User className="h-4 w-4" />
                      Profile Settings
                    </button>
                    <button
                      onClick={() => {
                        navigate('/app/settings?tab=security');
                        setShowProfileDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      Account Settings
                    </button>
                  </div>
                  <div className="border-t border-gray-200 py-2">
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NewNavigation;
